import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Shield, ShieldCheck, Clock, AlertTriangle } from "lucide-react";
import React from "react";
import { useCreateIdentityVerificationSessionMutation, useGetHomeQuery } from "@/store/rest_api";
import { toast } from "sonner";

export function KYCVerification() {
  const { data: user } = useGetHomeQuery();
  const [createSession, { isLoading }] = useCreateIdentityVerificationSessionMutation();

  const kycStatus = user?.activeOrganisation?.kycVerificationStatus;

  const handleStartVerification = async () => {
    try {
      const result = await createSession().unwrap();
      window.location.href = result.url;
    } catch (error) {
      toast.error("Failed to start verification");
    }
  };

  const getStatusDisplay = () => {
    switch (kycStatus) {
      case "PENDING":
        return {
          icon: <Clock className="h-4 w-4 text-warning" />,
          badge: <span className="status-pill-warning">Pending</span>,
          title: "Verification Pending",
          description: "Your verification is being processed. This usually takes a few minutes.",
        };
      case "FAILED":
        return {
          icon: <AlertTriangle className="h-4 w-4 text-destructive" />,
          badge: <span className="status-pill-destructive">Failed</span>,
          title: "Verification Failed",
          description: "We could not verify your identity. Please try again.",
        };
      case "REQUIRES_INPUT":
        return {
          icon: <AlertTriangle className="h-4 w-4 text-warning" />,
          badge: <span className="status-pill-warning">Action Required</span>,
          title: "Additional Information Required",
          description: "Please complete the verification process.",
        };
      default:
        return {
          icon: <Shield className="h-4 w-4 text-muted-foreground" />,
          badge: <span className="status-pill-neutral">Not Started</span>,
          title: "Identity Verification",
          description: "Complete identity verification before choosing a plan.",
        };
    }
  };

  if (kycStatus === "VERIFIED") {
    return (
      <Card className="flex items-center gap-3 px-4 py-3">
        <ShieldCheck className="h-5 w-5 text-success" />
        <span className="font-semibold">Identity verified</span>
        <span className="status-pill-success ml-auto">Verified</span>
      </Card>
    );
  }

  const statusDisplay = getStatusDisplay();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {statusDisplay.title}
            </CardTitle>
          </div>
          {statusDisplay.badge}
        </div>
        <CardDescription>{statusDisplay.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertTitle className="flex items-center gap-2">
            {statusDisplay.icon}
            What you'll need
          </AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>A government-issued ID (passport, driver's license, or national ID)</li>
              <li>A smartphone or webcam to take photos</li>
              <li>5-10 minutes to complete the process</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Button onClick={handleStartVerification} disabled={isLoading}>
          {isLoading
            ? "Starting..."
            : kycStatus === "FAILED" || kycStatus === "PENDING"
              ? "Retry Verification"
              : "Start Verification"}
        </Button>
      </CardContent>
    </Card>
  );
}
